const win = window as { [key: string]: any };

// // todo ENSURE THIS DOES NOT END UP IN PRODUCTION BUILD
// if (typeof win !== 'undefined') {
// 	const runtime = require('react-refresh/runtime');
// 	win['react_refresh_runtime'] = runtime;
// 	runtime.injectIntoGlobalHook(window);
// 	win.$RefreshReg$ = () => {};
// 	win.$RefreshSig$ = () => (type: any) => type;
// }

import * as Sentry from '@sentry/browser';
import * as FoxExtensions from 'fox-extensions';
import { IRefreshTokenApiResponse, IExtensionRegistration, AsyncExtensionsMaster, updateIdentity } from 'fox-extensions';

import fetch from 'cross-fetch';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactRouter from 'react-router';
import * as ReactRouterDOM from 'react-router-dom';
import HostApp, { Routing } from '../App';

const { ExtensionContextProvider, extensionRegistration, fetchManifestUrl, loadScript } = FoxExtensions;

// magic webpack functions / variables
declare var __webpack_init_sharing__: (scope: string) => Promise<void>;
declare var __webpack_share_scopes__: { default: unknown };

type WebpackContainer = {
	get: (module: string) => Promise<() => { initPlugin: (init: IExtensionRegistration) => void }>;
	init: (shareDefault: unknown) => Promise<void>;
};

Sentry.init({ dsn: win['sentry_dsn'], ignoreErrors: ['ResizeObserver loop limit exceeded'] });

async function ensureClientManifestLoaded() {
	if (typeof win['clientManifest'] !== 'undefined') {
		const c = win['clientManifest'];
		const base = c && c.substring(0, c.lastIndexOf('/') + 1);
		const manifestEntries = await fetchManifestUrl(c);

		// this should be an array of scripts
		// not ideal but we need to waterfall the scripts
		// this should only be during dev mode
		for (const s of manifestEntries) {
			// loadScript returns a promise for the script. This ensures all are loaded fully
			await loadScript((!base || s.startsWith(base) ? '' : base) + s);
		}
	}
}

// preload the config manifests etc
const manifest = typeof (win['manifest'] !== 'undefined') ? win['manifest'] : {};

// hacky UMD-like loading of these things
win['React'] = React;
win['ReactDOM'] = ReactDOM;
win['ReactRouter'] = ReactRouter;
win['ReactRouterDOM'] = ReactRouterDOM;
win['FoxExtensions'] = FoxExtensions;
win['SentryBrowser'] = Sentry;

function docReady(fn: () => void) {
	// see if DOM is already available
	if (document.readyState === 'complete' || document.readyState === 'interactive') {
		// call on next available tick
		setTimeout(fn, 1);
	} else {
		document.addEventListener('DOMContentLoaded', fn);
	}
}

async function refreshToken(path?: string): Promise<IRefreshTokenApiResponse> {
	try {
		const response = await fetch('/refresh_token', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ return_url: window.location.pathname + window.location.search + window.location.hash })
		});
		const result = await response.json();
		// if (result.redirect_url) {
		// 	window.location.href = result.redirect_url;
		// 	return false;
		// }
		return result;
	} catch (e) {
		// alert('An error occurred with your session.');
		return { ok: false, error: e };
	}
}

const initialTokenPromise = refreshToken();

docReady(async () => {
	await ensureClientManifestLoaded();

	await __webpack_init_sharing__('default');
	// expose the container to window for easier inspection
	window['__FOX_HOST_CONTAINER'] = __webpack_share_scopes__.default;

	const containers: Record<string, WebpackContainer> = {};

	async function initContainer(name: string) {
		if (containers[name]) return containers[name];

		const container = win[name] as WebpackContainer | undefined;

		if (!container || typeof container.init !== 'function') return;

		await container.init(__webpack_share_scopes__.default);

		containers[name] = container;
		return container;
	}
	async function initPlugin(container: WebpackContainer, entrypoint: string, context: IExtensionRegistration) {
		if (!container) return false;

		try {
			const factory = await container.get(entrypoint);
			const module = factory();
			if (module.initPlugin) {
				module.initPlugin(context);
				return true;
			}
		} catch (e) {
			console.error('Failed to load module', e);
		}
		return false;
	}

	const initial = updateIdentity(await initialTokenPromise);

	for (const plugin of manifest) {
		const extContext = extensionRegistration(plugin);

		try {
			Sentry.addBreadcrumb({ message: `Loading plugin ${plugin.module}` });
			console.log(`Loading plugin ${plugin.module}`);

			// this is the old way of registering stuff
			if (win[plugin.module] && typeof (win[plugin.module] as any)['initPlugin'] === 'function') {
				(win[plugin.module] as any)['initPlugin'](extContext);
			} else if (await initPlugin(await initContainer(plugin.module), 'entrypoint', extContext)) {
				// production module
			} else if (await initPlugin(await initContainer('SwiftFoxDev'), plugin.module, extContext)) {
				// dev time single build
			} else {
				console.warn('Plugin ' + plugin.name + ' did not provide an initPlugin function');
			}
		} catch (e) {
			Sentry.captureException(e);
			console.warn(`Plugin ${plugin.name} had error`, e);
		}
	}

	// the ordering of contexts here is important
	// AsyncExtensions depend on both the extension context and identity context
	// IdentityContextProvider depends on extension context so we can have non-authenticated extensible UI
	ReactDOM.render(
		<React.Suspense fallback={<div />}>
			<ReactRouterDOM.BrowserRouter>
				<ExtensionContextProvider host={window.location.host} isServer={false} manifest={manifest}>
					<FoxExtensions.IdentityContextProvider refreshToken={refreshToken} initialToken={initial}>
						<AsyncExtensionsMaster>
							<Routing />
						</AsyncExtensionsMaster>
					</FoxExtensions.IdentityContextProvider>
				</ExtensionContextProvider>
			</ReactRouterDOM.BrowserRouter>
		</React.Suspense>,
		document.getElementById('root')
	);
});
